import { Box, Button, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FormLoaderWrapper from 'src/shared/components/organisms/formLoaderWrapper';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { toast } from 'react-toastify';
import {
  getOrganizationBoard,
  getOrganizationRoles,
  getOrganizationTitleConfigByType,
  updateOrganizationBoardTitle,
  updateOrganizationBoardTitleConfig,
  updateOrganizationRoleTitle,
  updateOrganizationRoleTitleConfig,
} from 'src/shared/services/organizationService';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { OrganizationTitleTypeEnum } from 'src/shared/models/enums/organizationTitleType.enum';
import { useGlobals } from 'src/shared/contexts/appContext';
import { OrganizationRoleTable } from 'src/shared/components/organisms/organizationRoleTable';
import { Edit } from '@mui/icons-material';
import { OrganizationTitleConfigModal } from 'src/shared/components/organisms/organizationTitleConfigModal';
import { OrginizationResignConfirm } from 'src/shared/components/organisms/organizationResignConfirm';
import { MemberSimpleSearchModal } from 'src/modules/member/components/memberSimpleSearchModal';
import { OrganizationTitleEditDateModal } from 'src/shared/components/organisms/organizationTitleEditDateModal';
import { OrganizationTitleResponse } from '../models/organizationTitleResponse';
import { OrganizationTitleRequest } from '../models/organizationTitleRequest';

type OrganizationTitlePageProps = {
  titleType: OrganizationTitleTypeEnum;
};

export const OrganizationTitlePage = (props: OrganizationTitlePageProps) => {
  const { titleType } = props;
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const { dateStamp, setDateStamp } = useGlobals();

  const [title, setTitle] = useState<OrganizationTitleResponse[]>([]);
  const [selectedMemberTitleId, setSelectedMemberTitleId] = useState<number>();
  const [selectedMemberTitle, setSelectedMemberTitle] = useState<OrganizationTitleResponse>();

  const [showUserModal, setShowUserModal] = useState<boolean>(false);
  const [showResignModal, setShowResignModal] = useState<boolean>(false);
  const [showTitleConfigModal, setShowTitleConfigModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);

  const isMounted = useIsMounted();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      let result;
      try {
        if (titleType === OrganizationTitleTypeEnum.Styrelse) {
          result = await getOrganizationBoard();
        } else {
          result = await getOrganizationRoles();
        }
        if (isMounted()) {
          setTitle(result.data);
          ConsoleHelper.log(dateStamp);
        }
      } catch (e) {
        if (isMounted()) ConsoleHelper.log(e);
      } finally {
        if (isMounted()) setLoading(false);
      }
    };
    fetchData();
  }, [isMounted, dateStamp, titleType]);

  const handleMemberSelect = async (memberId: number) => {
    setShowUserModal(false);
    const requestData: OrganizationTitleRequest = {
      memberTitle_id: selectedMemberTitleId || 0,
      member_id: memberId,
      admDate: memberId > 0 ? DateHelper.getFSDate(new Date()) : '',
      resDate: null,
    };
    try {
      setLoading(true);
      if (titleType === OrganizationTitleTypeEnum.Styrelse) {
        await updateOrganizationBoardTitle(requestData);
      } else {
        await updateOrganizationRoleTitle(requestData);
      }

      if (isMounted()) {
        toast.success(<>{t('common:statusMessages.saveChangesSuccess')}</>);
        setDateStamp(new Date());
      }
    } catch (e) {
      if (isMounted()) ConsoleHelper.log(e);
    } finally {
      if (isMounted()) setLoading(false);
    }
  };

  const handleResign = () => {
    handleMemberSelect(0);
  };

  return (
    <FormLoaderWrapper loading={loading}>
      <Box sx={{ p: 3, minHeight: '15rem', maxWidth: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant='h6' gutterBottom>
            {titleType === OrganizationTitleTypeEnum.Styrelse ? t('common:organization.boardTitle') : t('common:organization.electedTitle')}
          </Typography>
          <Box>
            <Button variant='outlined' size='small' startIcon={<Edit />} onClick={() => setShowTitleConfigModal(true)}>
              {titleType === OrganizationTitleTypeEnum.Styrelse ? t('common:organization.editBoard') : t('common:organization.editElected')}
            </Button>
          </Box>
        </Box>
        {title && (
          <OrganizationRoleTable<OrganizationTitleResponse>
            roles={title}
            idColumn='memberTitle_id'
            canUseColumn='canUse'
            titleColumn='title'
            valueColumn='name'
            admDateColumn='admDate'
            resDateColumn='resDate'
            setUserCallback={(titleId) => {
              setSelectedMemberTitleId(titleId);
              setShowUserModal(true);
            }}
            editDateCallback={(titleId) => {
              const selectedTitle = title.find((x) => x.memberTitle_id === titleId);
              setSelectedMemberTitle(selectedTitle);
              setShowEditModal(true);
            }}
            deleteCallback={(titleId) => {
              setSelectedMemberTitleId(titleId);
              setShowResignModal(true);
            }}
          />
        )}
        {showTitleConfigModal && (
          <OrganizationTitleConfigModal
            modalOpen={showTitleConfigModal}
            onClose={() => setShowTitleConfigModal(false)}
            onSave={() => {
              setShowTitleConfigModal(false);
              setDateStamp(new Date());
            }}
            modalTitle={titleType === OrganizationTitleTypeEnum.Styrelse ? t('common:organization.editBoard') : t('common:organization.editElected')}
            type={titleType}
            organizationId={undefined}
            fetchMethod={async () => {
              return getOrganizationTitleConfigByType(titleType);
            }}
            updateMethod={async (data) => {
              if (titleType === OrganizationTitleTypeEnum.Styrelse) {
                return updateOrganizationBoardTitleConfig(data);
              }
              return updateOrganizationRoleTitleConfig(data);
            }}
          />
        )}
        <OrginizationResignConfirm
          modalOpen={showResignModal}
          onClose={() => setShowResignModal(false)}
          onConfirm={() => {
            setShowResignModal(false);
            handleResign();
          }}
        />
        <MemberSimpleSearchModal
          modalOpen={showUserModal}
          onClose={() => setShowUserModal(false)}
          onSelectMember={(memberId) => handleMemberSelect(memberId)}
        />
        {selectedMemberTitle && showEditModal && (
          <OrganizationTitleEditDateModal
            modalOpen={showEditModal}
            modalTitle={
              titleType === OrganizationTitleTypeEnum.Styrelse ? t('common:organization.editBoardUser') : t('common:organization.editElectedUser')
            }
            organizationTitleResponse={selectedMemberTitle}
            updateMethod={async (data) => {
              if (titleType === OrganizationTitleTypeEnum.Styrelse) {
                return updateOrganizationBoardTitle(data);
              }
              return updateOrganizationRoleTitle(data);
            }}
            onClose={() => setShowEditModal(false)}
            onSave={() => {
              setShowEditModal(false);
              setDateStamp(new Date());
            }}
          />
        )}
      </Box>
    </FormLoaderWrapper>
  );
};
